<template>
  <div class="adjustment-page-wrapper">
    <div class="adjustment-page-container">
      <!-- MAIN 2-COLUMN WORKSPACE (Matches /manage-waste Layout) -->
      <v-row class="fill-height ma-0" no-gutters>
        <!-- LEFT PANEL: Product / Ingredient Catalog with Search & Source Switcher -->
        <v-col cols="12" md="4" lg="3" class="d-flex flex-column pr-md-3 pb-3 pb-md-0 fill-height">
          <v-card
            class="ap-card product-card flex-grow-1 d-flex flex-column fill-height"
            outlined
            elevation="0"
          >
            <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom flex-shrink-0 ap-card-title">
              <div class="d-flex align-center" style="gap: 10px;">
                <span class="ap-card-accent"></span>
                <span class="font-weight-bold text-subtitle-2">
                  {{ activeTab === "ingredient" ? $t("ingredient_item") : $t("product_item") }}
                </span>
              </div>
              <v-chip x-small color="primary" outlined class="font-weight-bold">
                {{ currentRows.length }} {{ $t("items") || "items" }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-2 px-3 pb-2 d-flex flex-column flex-grow-1 overflow-hidden" style="min-height: 0;">
              <!-- Search & Source Switcher -->
              <div class="filter-controls mb-2 flex-shrink-0">
                <TextSearch
                  v-model="search"
                  :placeholder="activeTab === 'ingredient' ? 'search_ingredient' : 'search_product'"
                  @search="fetchItem()"
                  @onClear="clearSearch()"
                />

                <!-- Category / Source Toggle Tabs (Matches /manage-waste .br-tabs style) -->
                <div v-if="canAdjustIngredients" class="waste-source-tabs-wrap mt-2">
                  <div class="br-tabs">
                    <div
                      class="br-tab"
                      :class="{ 'br-tab--active': activeTab === 'product' }"
                      @click="activeTab = 'product'"
                    >
                      <v-icon left x-small class="mr-1" :color="activeTab === 'product' ? 'primary' : '#94a3b8'">
                        fal fa-box
                      </v-icon>
                      {{ $t("product_item") }}
                    </div>
                    <div
                      class="br-tab"
                      :class="{ 'br-tab--active': activeTab === 'ingredient' }"
                      @click="activeTab = 'ingredient'"
                    >
                      <v-icon left x-small class="mr-1" :color="activeTab === 'ingredient' ? 'primary' : '#94a3b8'">
                        fal fa-mortar-pestle
                      </v-icon>
                      {{ $t("ingredient_item") }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Product / Item List Container -->
              <div class="product-list-container flex-grow-1 overflow-y-auto pr-1">
                <!-- Skeletons when loading -->
                <template v-if="overlay && !currentRows.length">
                  <v-skeleton-loader
                    v-for="n in 6"
                    :key="n"
                    type="list-item-avatar-two-line"
                    class="my-1"
                  />
                </template>

                <!-- Item Cards (Matches /manage-waste style) -->
                <template v-else-if="currentRows.length">
                  <div
                    v-for="row in currentRows"
                    :key="row.cartKey"
                    class="product-item-card mb-2"
                    :class="{ 'is-in-cart': isProductInCart(row) }"
                    @click="addToCart(row)"
                    :title="$t('click_to_add_product')"
                  >
                    <!-- Left Area: Thumb + Details -->
                    <div class="product-card-left">
                      <div class="product-thumb-box">
                        <app-img
                          :img="row.img"
                          width="46"
                          height="46"
                          class="product-thumb-img"
                        />
                        <span v-if="isProductInCart(row)" class="cart-count-badge">
                          <v-icon x-small color="white">fal fa-check</v-icon>
                        </span>
                      </div>

                      <div class="product-info-box">
                        <div class="product-name-text text-truncate">
                          {{ activeTab === "ingredient" ? ingredientName(row) : row.name }}
                        </div>

                        <div class="product-sub-row">
                          <span v-if="row.code" class="product-code-mono">{{ row.code }}</span>
                          <span v-if="row.code" class="product-dot-sep">•</span>
                          <!-- Stock status pill -->
                          <span v-if="!(Number(rowStock(row)) > 0)" class="stock-pill stock-pill-out">
                            {{ $t("out_of_stock") }} (0)
                          </span>
                          <span v-else-if="Number(rowStock(row)) <= 10" class="stock-pill stock-pill-low">
                            {{ $t("low_stock") }} ({{ rowStock(row) }})
                          </span>
                          <span v-else class="stock-pill-normal">
                            {{ $t("stock") }}: {{ rowStock(row) }}
                            <template v-if="row.unit_name">{{ row.unit_name }}</template>
                          </span>
                        </div>

                        <div v-if="row.variant_label" class="product-price-row mt-1">
                          <v-chip x-small label outlined color="primary">
                            {{ row.variant_label }}
                          </v-chip>
                        </div>
                      </div>
                    </div>

                    <!-- Right Area: Add Button -->
                    <button
                      type="button"
                      class="product-add-btn"
                      :class="{ 'btn-in-cart': isProductInCart(row) }"
                      :title="$t('add')"
                      @click.stop="addToCart(row)"
                    >
                      <v-icon small class="add-btn-icon" :color="isProductInCart(row) ? 'white' : ''">
                        {{ isProductInCart(row) ? "fal fa-check" : "fal fa-plus" }}
                      </v-icon>
                    </button>
                  </div>
                </template>

                <!-- Empty State -->
                <div v-else class="ps-empty text-center py-8">
                  <v-icon size="48" class="ps-empty-icon mb-2">fal fa-box-open</v-icon>
                  <div class="ps-empty-text font-weight-bold">
                    {{ activeTab === "ingredient" ? $t("no_ingredients_found") : $t("no_products_found") }}
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RIGHT PANEL: Adjustment Cart Table & HUD -->
        <v-col cols="12" md="8" lg="9" class="d-flex flex-column pl-md-0">
          <v-card
            class="ap-card purchase-main-card flex-grow-1 d-flex flex-column fill-height"
            outlined
            elevation="0"
          >
            <!-- Cart Header -->
            <v-card-title class="py-3 px-4 border-bottom d-flex align-center justify-space-between flex-shrink-0 ap-card-title">
              <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                <span class="ap-card-accent"></span>
                <span class="font-weight-bold text-subtitle-2">{{ $t("stock_adjustment") }}</span>
                <span class="ap-card-subtitle d-none d-sm-inline">
                  ({{ desserts.length }} {{ $t("items") || "items" }})
                </span>
              </div>

              <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <v-btn
                  v-if="desserts.length > 0"
                  text
                  small
                  color="error"
                  class="font-weight-bold text-caption rounded-lg"
                  @click="clearAllItems"
                >
                  <v-icon left small>fal fa-trash</v-icon>
                  {{ $t("clear_selected") }}
                </v-btn>

                <v-btn
                  outlined
                  small
                  rounded
                  color="primary"
                  class="text-none font-weight-medium"
                  to="/report-stock-adjustment"
                >
                  <v-icon left x-small>fal fa-list</v-icon>
                  {{ $t("report_stock_adjustment") }}
                </v-btn>
              </div>
            </v-card-title>

            <!-- Scrollable Content Table -->
            <div class="cart-content-area flex-grow-1 overflow-auto">
              <v-data-table
                :headers="headers"
                :items="desserts"
                item-key="cartKey"
                hide-default-footer
                class="purchase-table elevation-0"
                fixed-header
                :loading="overlay"
                :items-per-page="100"
              >
                <!-- Row Index -->
                <template slot="item.index" slot-scope="{ index }">
                  <span class="ps-row-index">{{ index + 1 }}</span>
                </template>

                <!-- Product / Item Name & Metadata -->
                <template slot="item.name" slot-scope="{ item }">
                  <div class="d-flex align-center py-2">
                    <span class="ps-row-thumb mr-3 shrink-0">
                      <v-avatar tile size="38" class="rounded-lg ps-thumb-avatar">
                        <app-img :img="item.img" />
                      </v-avatar>
                    </span>
                    <div class="ps-row-name min-w-0">
                      <span
                        class="ps-product-name text-truncate"
                        :title="item.itemType === 'ingredient' ? ingredientName(item) : item.name"
                      >
                        {{ item.itemType === "ingredient" ? ingredientName(item) : item.name }}
                      </span>
                      <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                        <span v-if="item.code" class="ps-product-code">{{ item.code }}</span>
                        <v-chip
                          v-if="item.variant_label"
                          x-small
                          label
                          outlined
                          color="primary"
                        >
                          {{ item.variant_label }}
                        </v-chip>
                      </div>

                      <!-- Stock calculation preview -->
                      <div class="stock-change-hint d-flex align-center text-caption mt-1">
                        <span class="grey--text text--darken-1">{{ $t("stock") }}: {{ item.stock }}</span>
                        <v-icon x-small class="mx-1">fal fa-long-arrow-right</v-icon>
                        <span :class="resultStock(item) < 0 ? 'error--text font-weight-bold' : 'font-weight-medium primary--text'">
                          {{ resultStock(item) }}
                        </span>
                        <span v-if="item.unit_name" class="ml-1 grey--text">{{ item.unit_name }}</span>
                      </div>

                      <!-- Stock alert if negative -->
                      <div v-if="resultStock(item) < 0" class="error--text text-caption font-weight-bold d-flex align-center mt-1" role="alert">
                        <v-icon x-small color="error" class="mr-1">fal fa-exclamation-triangle</v-icon>
                        {{ $t("invalid_stock_calculation") }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Direction Toggle (Increase / Decrease) -->
                <template slot="item.direction" slot-scope="{ item }">
                  <div class="d-flex justify-center">
                    <v-btn-toggle
                      v-model="item.direction"
                      mandatory
                      dense
                      class="ps-status-toggle"
                    >
                      <v-btn value="increase" x-small class="text-none">
                        <v-icon left x-small color="success">fal fa-plus</v-icon>
                        {{ $t("increase") }}
                      </v-btn>
                      <v-btn value="decrease" x-small class="text-none">
                        <v-icon left x-small color="error">fal fa-minus</v-icon>
                        {{ $t("decrease") }}
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </template>

                <!-- Quantity Stepper -->
                <template slot="item.qty" slot-scope="{ item }">
                  <div class="d-flex justify-center">
                    <div class="ps-qty-stepper" :class="{ 'error-border': resultStock(item) < 0 }">
                      <button
                        type="button"
                        class="ps-qty-btn"
                        :disabled="overlay"
                        @click="stepQty(item, -1)"
                        title="Decrease quantity"
                      >
                        <v-icon x-small>fal fa-minus</v-icon>
                      </button>
                      <input
                        v-model.number="item.qty"
                        type="number"
                        :min="item.itemType === 'ingredient' ? 0.001 : 1"
                        :step="item.itemType === 'ingredient' ? 0.001 : 1"
                        class="ps-qty-input"
                      />
                      <button
                        type="button"
                        class="ps-qty-btn ps-qty-btn--plus"
                        :disabled="overlay"
                        @click="stepQty(item, 1)"
                        title="Increase quantity"
                      >
                        <v-icon x-small>fal fa-plus</v-icon>
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Result Stock -->
                <template slot="item.result_stock" slot-scope="{ item }">
                  <div class="text-center font-weight-bold" :class="resultStock(item) < 0 ? 'error--text' : ''">
                    {{ resultStock(item) }}
                  </div>
                </template>

                <!-- Note / Description -->
                <template slot="item.note" slot-scope="{ item }">
                  <v-text-field
                    v-model="item.note"
                    dense
                    outlined
                    hide-details
                    class="ps-table-input"
                    :placeholder="$t('description')"
                  />
                </template>

                <!-- Actions -->
                <template slot="item.actions" slot-scope="{ index }">
                  <div class="d-flex justify-center">
                    <button
                      type="button"
                      class="ps-act ps-act--danger"
                      :title="$t('delete')"
                      @click="removeItem(index)"
                    >
                      <v-icon x-small>fal fa-trash-alt</v-icon>
                    </button>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Bottom HUD Section (Reason & Summary Metrics) -->
            <div class="cart-summary-area pa-3 border-top shrink-0">
              <!-- Reason input card -->
              <v-card outlined elevation="0" class="ap-card pa-3 mb-3 bg-slate-50">
                <v-row dense align="center">
                  <v-col cols="12">
                    <div class="d-flex align-center mb-1">
                      <span class="font-weight-bold text-caption">{{ $t("reason") }}</span>
                      <span class="error--text ml-1">*</span>
                    </div>
                    <v-text-field
                      v-model="reason"
                      outlined
                      dense
                      hide-details="auto"
                      :placeholder="$t('reason_placeholder')"
                      :error-messages="reasonError"
                      prepend-inner-icon="fal fa-comment-alt-lines"
                      class="ps-reason-input"
                    />
                  </v-col>
                </v-row>
              </v-card>

              <!-- Metrics Summary Cards & Action Submit Button -->
              <v-row dense align="center" justify="space-between">
                <v-col cols="12" sm="7" md="8">
                  <v-row dense>
                    <v-col cols="4">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--primary">
                          <v-icon small color="primary">fal fa-boxes</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("items") || "Items" }}</div>
                          <div class="ps-summary-value">{{ desserts.length }}</div>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="4">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--info">
                          <v-icon small color="info">fal fa-arrow-up</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("increase") }}</div>
                          <div class="ps-summary-value ps-summary-value--info">{{ totalIncreaseCount }}</div>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="4">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--danger">
                          <v-icon small color="error">fal fa-arrow-down</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("decrease") }}</div>
                          <div class="ps-summary-value ps-summary-value--danger">{{ totalDecreaseCount }}</div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" sm="5" md="4" class="text-right">
                  <v-btn
                    color="primary"
                    x-large
                    block
                    @click="submitRequest()"
                    :disabled="!desserts.length"
                    :loading="overlay"
                    class="font-weight-bold text-none rounded-lg elevation-1"
                  >
                    <v-icon left size="20">fal fa-paper-plane</v-icon>
                    {{ $t("submit_stock_adjustment") }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <app-loading :overlay="overlay" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { confirm, toast, error } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import _ from "lodash";

export default {
  mixins: [myMixin],

  data() {
    return {
      overlay: false,
      activeTab: "product",
      search: "",
      desserts: [],
      ingredients: { data: [] },
      reason: "",
      reasonError: "",
    };
  },

  created() {
    this.fetchItem();
    this.searchDebounce = _.debounce(this.fetchItem, 500);
  },

  computed: {
    headers() {
      return [
        { text: "#", value: "index", align: "center", sortable: false, width: "45px" },
        { text: this.$t("name"), value: "name", sortable: false },
        { text: this.$t("direction"), value: "direction", align: "center", sortable: false, width: "170px" },
        { text: this.$t("quantity"), value: "qty", align: "center", sortable: false, width: "135px" },
        { text: this.$t("result_stock"), value: "result_stock", align: "center", sortable: false, width: "110px" },
        { text: this.$t("description"), value: "note", sortable: false, width: "180px" },
        { text: "", value: "actions", align: "center", sortable: false, width: "60px" },
      ];
    },

    ...mapGetters({
      products: "index/product_purchase",
    }),

    canAdjustIngredients() {
      return this.MyRole("create_ingredient_adjustment");
    },

    currentRows() {
      return this.activeTab === "ingredient" ? this.ingredientRows : this.productRows;
    },

    totalIncreaseCount() {
      return this.desserts.filter((item) => item.direction === "increase").length;
    },

    totalDecreaseCount() {
      return this.desserts.filter((item) => item.direction === "decrease").length;
    },

    // A variant parent's own `stock` is unused — the backend requires a
    // variant_id for it — so each active variant becomes its own pickable row
    // (products_all already returns them under `variants`), and the parent
    // itself is never listed.
    productRows() {
      const rows = [];
      (this.products.data || []).forEach((product) => {
        if (!product.has_variations) {
          rows.push({
            ...product,
            product_id: product.id,
            variant_id: null,
            variant_label: "",
            cartKey: `p-${product.id}`,
          });
          return;
        }
        (product.variants || []).forEach((variant) => {
          rows.push({
            ...product,
            product_id: product.id,
            variant_id: variant.id,
            variant_label: variant.label || variant.sku || "",
            stock: variant.stock,
            cartKey: `p-${product.id}-v-${variant.id}`,
          });
        });
      });
      return rows;
    },

    ingredientRows() {
      return (this.ingredients.data || []).map((ingredient) => ({
        ...ingredient,
        stock: ingredient.current_stock,
        cartKey: `i-${ingredient.id}`,
      }));
    },
  },

  watch: {
    // Switching tabs must never leave a row from the other item type sitting
    // in the cart — submitRequest()'s guard would catch it, but clearing
    // here means the cart just reflects whichever tab is showing.
    activeTab() {
      this.desserts = [];
      this.reason = "";
      this.search = "";
      this.fetchItem();
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),

    rowStock(row) {
      if (this.activeTab === "ingredient") {
        return row.current_stock != null ? row.current_stock : row.stock;
      }
      return row.stock;
    },

    clearSearch() {
      this.search = "";
      this.fetchItem();
    },

    clearAllItems() {
      this.desserts = [];
    },

    async fetchItem() {
      this.overlay = true;
      if (this.activeTab === "ingredient") {
        await this.fetchIngredients();
      } else {
        const body = {
          that: this,
          // has_bom=0: a recipe product's stock lives in its ingredients
          // (Ingredient item tab), never in products/product_variants.
          url: `products_all?q=${this.search}&per_page=100&track_stock=1&is_bundle=0&has_bom=0`,
          commit: "SET_PRODUCT_PURCHASE",
        };
        await this.fetch(body);
      }
      this.overlay = false;
    },

    async fetchIngredients() {
      try {
        const { data } = await this.$http.get(
          `ingredients_paginate?q=${this.search}&per_page=100`
        );
        if (data.status) {
          this.ingredients = data.data;
        }
      } catch (err) {
        toast(this, "error_submitting_request", "error");
      }
    },

    ingredientName(item) {
      const locale = this.$i18n.locale;
      if (locale === "en" && item.name_en) return item.name_en;
      if (locale === "th" && item.name_th) return item.name_th;
      if ((locale === "cn" || locale === "ch") && item.name_cn) {
        return item.name_cn;
      }
      return item.name_la || item.name_en || item.name_th || item.name_cn || "-";
    },

    isProductInCart(item) {
      return this.desserts.some((d) => d.cartKey === item.cartKey);
    },

    addToCart(item) {
      const duplicate = this.desserts.find((d) => d.cartKey === item.cartKey);
      if (duplicate) {
        toast(this, "item_already_in_list", "warning");
        return;
      }

      const stock = this.activeTab === "ingredient" ? item.current_stock : item.stock;

      this.desserts.push({
        ...item,
        stock: stock != null ? stock : 0,
        direction: "increase",
        qty: 1,
        note: "",
        itemType: this.activeTab,
      });
    },

    stepQty(item, delta) {
      const isIngredient = item.itemType === "ingredient";
      const step = isIngredient ? (Math.abs(delta) < 1 ? delta : delta * 1) : delta;
      const current = parseFloat(item.qty) || 0;
      const min = isIngredient ? 0.001 : 1;
      const next = Math.max(min, Math.round((current + step) * 1000) / 1000);
      item.qty = next;
    },

    removeItem(index) {
      this.desserts.splice(index, 1);
    },

    resultStock(item) {
      const qty = parseFloat(item.qty) || 0;
      const delta = item.direction === "decrease" ? -qty : qty;
      const baseStock = parseFloat(item.stock) || 0;
      const res = baseStock + delta;
      return Math.round(res * 1000) / 1000;
    },

    async submitRequest() {
      this.reasonError = "";
      if (!this.reason || !this.reason.trim()) {
        this.reasonError = this.$t("reason_required");
        return;
      }

      if (!this.desserts.length) {
        toast(this, "no_items_to_adjust", "warning");
        return;
      }

      const negativeResult = this.desserts.find((item) => this.resultStock(item) < 0);
      if (negativeResult) {
        error(this, "invalid_stock_calculation");
        return;
      }

      // Mirrors the backend's between:-1000000,1000000 rule
      const outOfRange = this.desserts.find(
        (item) => Math.abs(parseFloat(item.qty) || 0) > 1000000
      );
      if (outOfRange) {
        error(this, "qty_out_of_range");
        return;
      }

      // Defensive check for mismatched item type
      const mismatched = this.desserts.find((item) => item.itemType !== this.activeTab);
      if (mismatched) {
        error(this, "adjustment_type_mismatch");
        return;
      }

      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_submit_stock_adjustment",
      });
      if (!isConfirmed) return;

      try {
        this.overlay = true;

        const endpoint = this.activeTab === "ingredient" ? "ingredient_adjustment" : "stock_adjustment";
        const data = this.desserts.map((item) => {
          const qty = parseFloat(item.qty) || 0;
          const signedQty = item.direction === "decrease" ? -qty : qty;

          if (this.activeTab === "ingredient") {
            return {
              ingredient_id: item.id,
              qty: signedQty,
              note: item.note || null,
            };
          }

          return {
            product_id: item.product_id,
            variant_id: item.variant_id || null,
            qty: signedQty,
            note: item.note || null,
          };
        });

        const form = { reason: this.reason, data };
        const { data: res } = await this.$http.post(endpoint, form);

        if (res.status) {
          this.desserts = [];
          this.reason = "";
          toast(this, res.message, "success");
        } else {
          toast(this, res.message || "error_submitting_request", "error");
        }
      } catch (err) {
        toast(this, "error_submitting_request", "error");
      } finally {
        this.overlay = false;
      }
    },
  },
};
</script>

<style scoped>
.adjustment-page-wrapper {
  height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
}

.adjustment-page-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
}

.cart-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border-radius: 12px;
}

/* PosSmart Section Cards (matches waste-management.vue & purchase.vue) */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
  background-color: #ffffff !important;
}

.ap-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 600;
}

.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
  flex-shrink: 0;
}

.ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}

.cart-summary-area {
  flex-shrink: 0;
  background-color: #ffffff;
  color: inherit;
  display: flex;
  flex-direction: column;
}

.product-card {
  height: 100%;
  min-height: 0;
}

.product-card >>> .v-card__text {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.purchase-main-card {
  height: 100%;
  min-height: 0;
}

.filter-controls {
  flex-shrink: 0;
}

.product-list-container {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  border-radius: 12px;
  padding: 2px 4px 4px 2px;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-slate-50 {
  background-color: #f8fafc !important;
}

/* Category / Source Toggle Tabs (Matches /manage-waste .br-tabs style) */
.waste-source-tabs-wrap {
  width: 100%;
}

.waste-source-tabs-wrap .br-tabs {
  display: flex;
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
}

.waste-source-tabs-wrap .br-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: #64748b;
  transition: all 0.15s ease;
}

.waste-source-tabs-wrap .br-tab:hover {
  color: #141021;
}

.waste-source-tabs-wrap .br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}

/* Product Items in Left Panel */
.product-item-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  gap: 8px;
}

.product-item-card:hover {
  border-color: #5b39c9;
  box-shadow: 0 4px 12px rgba(91, 57, 201, 0.08);
  background-color: rgba(91, 57, 201, 0.02);
}

.product-item-card:hover .product-name-text {
  color: #5b39c9;
}

.product-item-card:hover .product-add-btn {
  background-color: #5b39c9;
  color: #ffffff;
}

.product-item-card:hover .product-add-btn .add-btn-icon {
  color: #ffffff !important;
}

.product-item-card.is-in-cart {
  background-color: rgba(91, 57, 201, 0.05);
  border-color: #5b39c9;
}

.product-card-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}

.product-thumb-box {
  position: relative;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
}

.product-thumb-img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
  background: #f1f5f9;
}

.cart-count-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #5b39c9;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
}

.product-info-box {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-name-text {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}

.product-sub-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
}

.product-code-mono {
  font-family: monospace;
  color: #94a3b8;
}

.product-dot-sep {
  color: #cbd5e1;
}

.stock-pill {
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
}

.stock-pill-out {
  background: #fef2f2;
  color: #ef4444;
}

.stock-pill-low {
  background: #fffbeb;
  color: #d97706;
}

.stock-pill-normal {
  background: #f0fdf4;
  color: #16a34a;
}

.product-price-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.product-add-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  flex: 0 0 28px;
  transition: all 0.15s ease;
  outline: none;
}

.product-add-btn .add-btn-icon {
  color: #64748b;
  transition: color 0.15s ease;
}

.product-add-btn:hover {
  background: #5b39c9;
  border-color: #5b39c9;
  color: #ffffff;
}

.product-add-btn:hover .add-btn-icon {
  color: #ffffff !important;
}

.product-add-btn.btn-in-cart {
  background: #5b39c9;
  border-color: #5b39c9;
  color: #ffffff;
}

.product-add-btn.btn-in-cart .add-btn-icon {
  color: #ffffff !important;
}

/* Table Styling */
.purchase-table >>> .v-data-table__wrapper table {
  border-collapse: collapse;
}

.purchase-table >>> .v-data-table__wrapper table thead th {
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid #e2e8f0;
  height: 38px;
  white-space: nowrap;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr {
  transition: background-color 0.15s ease;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: #f8fafc !important;
}

.purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid #f1f5f9;
  padding: 8px 12px;
}

.ps-row-index {
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.ps-row-thumb {
  position: relative;
  width: 38px;
  height: 38px;
  display: inline-flex;
}

.ps-thumb-avatar {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.ps-row-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ps-product-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-product-code {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-family: monospace;
  color: #94a3b8;
}

/* Direction Segment Toggle */
.ps-status-toggle {
  background: #f1f2f7 !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  padding: 2px !important;
}

.ps-status-toggle >>> .v-btn {
  border: none !important;
  border-radius: 6px !important;
  height: 26px !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  color: #64748b !important;
  background: transparent !important;
}

.ps-status-toggle >>> .v-btn--active {
  background: #ffffff !important;
  color: #1e293b !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
}

.ps-status-toggle >>> .v-btn[value="increase"].v-btn--active {
  color: #10b981 !important;
}

.ps-status-toggle >>> .v-btn[value="decrease"].v-btn--active {
  color: #ef4444 !important;
}

/* Quantity Stepper */
.ps-qty-stepper {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 2px;
  gap: 2px;
}

.error-border {
  border-color: #ef4444 !important;
}

.ps-qty-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-qty-btn:hover {
  color: #5b39c9;
  background: rgba(91, 57, 201, 0.08);
}

.ps-qty-btn--plus {
  color: #5b39c9;
}

.ps-qty-input {
  width: 52px;
  height: 24px;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1e293b;
  outline: none;
}

.ps-table-input >>> .v-input__slot {
  min-height: 32px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
  background: #f8fafc;
}

.ps-table-input >>> input {
  font-size: 12px;
}

.ps-act {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

/* Empty State */
.ps-empty {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ps-empty-icon {
  color: #cbd5e1 !important;
}

.ps-empty-text {
  font-size: 14px;
  color: #475569;
}

/* Bottom HUD: PosSmart Summary Metrics Cards */
.ps-summary-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  transition: all 0.15s ease;
  min-width: 0;
}

.ps-summary-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.ps-summary-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
}

.ps-summary-icon--primary {
  background: rgba(91, 57, 201, 0.1);
  color: #5b39c9;
  border-color: rgba(91, 57, 201, 0.2);
}

.ps-summary-icon--danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecdd3;
}

.ps-summary-icon--info {
  background: #f0fdf4;
  color: #10b981;
  border-color: #a7f3d0;
}

.ps-summary-meta {
  min-width: 0;
  flex: 1 1 auto;
}

.ps-summary-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.ps-summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.ps-summary-value--danger {
  color: #dc2626;
}

.ps-summary-value--info {
  color: #10b981;
}

.ps-reason-input >>> .v-input__slot {
  border-radius: 10px !important;
  background: #ffffff !important;
}

/* Dark Theme Overrides */
.theme--dark .ap-card {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background-color: #1e1e2d !important;
}

.theme--dark .ap-card-title {
  color: #ffffff;
}

.theme--dark .ap-card-subtitle {
  color: #94a3b8;
}

.theme--dark .cart-summary-area {
  background-color: #1e1e2d !important;
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .border-bottom {
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .border-top {
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .bg-slate-50 {
  background-color: #262638 !important;
}

.theme--dark .waste-source-tabs-wrap .br-tabs {
  background: #1b1d30;
  border-color: rgba(154, 140, 240, 0.16);
}

.theme--dark .waste-source-tabs-wrap .br-tab {
  color: #7c82a0;
}

.theme--dark .waste-source-tabs-wrap .br-tab--active {
  color: #9a8cf0 !important;
  background: rgba(154, 140, 240, 0.18) !important;
}

.theme--dark .product-item-card {
  background-color: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .product-item-card:hover {
  border-color: #6366f1;
  background-color: #252538;
}

.theme--dark .product-item-card.is-in-cart {
  background-color: rgba(91, 57, 201, 0.2);
  border-color: #818cf8;
}

.theme--dark .product-name-text {
  color: #f1f5f9;
}

.theme--dark .product-thumb-img {
  background: #262638;
}

.theme--dark .cart-count-badge {
  border-color: #1e1e2d;
}

.theme--dark .product-add-btn {
  background-color: #262638;
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

.theme--dark .product-add-btn .add-btn-icon {
  color: #94a3b8;
}

.theme--dark .stock-pill-out {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.theme--dark .stock-pill-low {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.theme--dark .stock-pill-normal {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table thead th {
  color: #94a3b8;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  color: #f1f5f9;
}

.theme--dark .ps-thumb-avatar {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .ps-product-name {
  color: #f1f5f9;
}

.theme--dark .ps-status-toggle {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .ps-status-toggle >>> .v-btn {
  color: #94a3b8 !important;
}

.theme--dark .ps-status-toggle >>> .v-btn--active {
  background: #1e1e2d !important;
  color: #ffffff !important;
}

.theme--dark .ps-qty-stepper {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .ps-qty-btn {
  background: #1e1e2d;
  color: #94a3b8;
}

.theme--dark .ps-qty-btn--plus {
  color: #a5b4fc;
}

.theme--dark .ps-qty-input {
  color: #ffffff;
}

.theme--dark .ps-table-input >>> .v-input__slot {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.theme--dark .ps-act {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #a5b4fc;
  border-color: #818cf8;
  background: rgba(129, 140, 248, 0.15);
}

.theme--dark .ps-act--danger:hover {
  color: #f87171;
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}

.theme--dark .ps-empty-icon {
  color: #475569 !important;
}

.theme--dark .ps-empty-text {
  color: #cbd5e1;
}

.theme--dark .ps-summary-card {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .ps-summary-card:hover {
  border-color: rgba(255, 255, 255, 0.16);
}

.theme--dark .ps-summary-icon--primary {
  background: rgba(129, 140, 248, 0.15);
  color: #a5b4fc;
  border-color: rgba(129, 140, 248, 0.25);
}

.theme--dark .ps-summary-icon--danger {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.25);
}

.theme--dark .ps-summary-icon--info {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  border-color: rgba(16, 185, 129, 0.25);
}

.theme--dark .ps-summary-value {
  color: #ffffff;
}

.theme--dark .ps-summary-value--danger {
  color: #f87171;
}

.theme--dark .ps-summary-value--info {
  color: #34d399;
}

.theme--dark .ps-reason-input >>> .v-input__slot {
  background: #1e1e2d !important;
}

@media (max-width: 960px) {
  .adjustment-page-wrapper {
    height: auto;
  }

  .adjustment-page-container {
    flex: none;
    padding: 8px;
  }

  .cart-content-area {
    flex: none;
    height: auto;
    max-height: 50vh;
  }

  .product-list-container {
    max-height: 380px;
  }
}
</style>
